import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff } from '../../../utils/constants';
import { selectAccessToken } from '../../../slices/staffSlice';
import { Loader } from '../../../components';
import { HugeiconsIcon } from '@hugeicons/react';
import { Search01Icon } from '@hugeicons/core-free-icons';
import { TONE_CLASS, deliveryStatus, localPhone } from './smsStatus';

// Messages the system sends to clients (repayment confirmations, approvals,
// and so on) with their delivery status. One-time codes are masked by the
// backend, and rows are deleted automatically after 30 days.

const TYPES = [
    ['', 'All messages'],
    ['repayment', 'Repayment confirmation'],
    ['paid_off', 'Loan paid off'],
    ['disbursement', 'Disbursement'],
    ['approval', 'Loan approved'],
    ['rejection', 'Loan rejected'],
    ['submission', 'Application received'],
    ['otp', 'Verification code'],
    ['welcome', 'Account created'],
];
const TYPE_LABEL = Object.fromEntries(TYPES);

const STATUS_FILTERS = [
    ['', 'All', 'total'],
    ['delivered', 'Delivered', 'delivered'],
    ['awaiting', 'Awaiting', 'awaiting'],
    ['failed', 'Not delivered', 'failed'],
    ['unknown', 'No report', 'unknown'],
];

function ClientMessages({ onExpired }) {
    const accessToken = useSelector(selectAccessToken);
    const [type, setType] = useState('');
    const [status, setStatus] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const load = useCallback(async () => {
        const res = await fetchRequests('/loans/staff/sms-messages', 'get', authStaff(accessToken), { page, type, status, q: search }, {});
        if (onExpired(res)) return;
        if (!res || res.err) { setError(res?.err || 'Could not load messages'); setData({ items: [], summary: {}, pages: 1, total: 0 }); return; }
        setError('');
        setData(res);
    }, [accessToken, page, type, status, search, onExpired]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[14rem]">
                    <HugeiconsIcon icon={Search01Icon} size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by phone number or loan ID"
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
                <select aria-label="Message type" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}
                    className="py-2 pl-3 pr-8 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                    {TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
            </div>

            <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map(([value, label, key]) => (
                    <button key={value} type="button" aria-pressed={status === value} onClick={() => { setStatus(value); setPage(1); }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md border ${status === value
                            ? 'bg-[#1e3a6e] text-white border-[#1e3a6e]'
                            : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                        {label} <span className="tabular-nums opacity-70">{(data?.summary?.[key] ?? 0).toLocaleString()}</span>
                    </button>
                ))}
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            {data === null ? <div className="py-10 flex justify-center"><Loader /></div> : data.items.length === 0 ? (
                <p className="text-sm text-gray-400 py-8 text-center">No messages found.</p>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium">Date</th>
                                <th className="px-4 py-3 text-left font-medium">Recipient</th>
                                <th className="px-4 py-3 text-left font-medium">Type</th>
                                <th className="px-4 py-3 text-left font-medium">Status</th>
                                <th className="px-4 py-3 text-left font-medium">Message</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                            {data.items.map((m) => {
                                const st = deliveryStatus(m.status);
                                return (
                                    <tr key={m._id} className="align-top">
                                        <td className="px-4 py-3 whitespace-nowrap text-gray-500">{new Date(m.createdAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="tabular-nums text-gray-800 dark:text-gray-100">{localPhone(m.phone)}</div>
                                            {m.loanId && <div className="text-xs text-gray-400">{m.loanId}</div>}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300">{TYPE_LABEL[m.type] || 'Other'}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${TONE_CLASS[st.tone]}`}>{st.label}</span>
                                            {m.error && <div className="mt-1 text-xs text-red-500 whitespace-normal max-w-[14rem]">{m.error}</div>}
                                        </td>
                                        <td className="px-4 py-3 min-w-[16rem] max-w-md">
                                            <button type="button" aria-expanded={expanded === m._id} title={expanded === m._id ? 'Show less' : 'Show full message'}
                                                onClick={() => setExpanded(expanded === m._id ? null : m._id)}
                                                className={`text-left text-gray-700 dark:text-gray-200 break-words ${expanded === m._id ? '' : 'line-clamp-2'}`}>
                                                {m.message}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {data?.pages > 1 && (
                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Page {page} of {data.pages} · {data.total.toLocaleString()} messages</span>
                    <div className="flex gap-2">
                        <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-40">Previous</button>
                        <button type="button" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-40">Next</button>
                    </div>
                </div>
            )}

            <p className="text-xs text-gray-400">Delivery status updates about every 5 minutes. Messages are deleted after 30 days.</p>
        </div>
    );
}

export default ClientMessages;
